import { use } from 'react';
import { useFieldArray, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';

import { CallPlanForm } from 'features/care-connect-settings/domain/models/call-plan-form';

type CallPlanExternalNumbers = {
  externalNumbers: ReturnType<typeof useFieldArray<CallPlanForm, 'externalNumbers'>>['fields'];
  externalNumbersEmptyErrorMessage: string | undefined;
  canAddExternalNumber: boolean;
  addExternalNumber: () => Promise<void>;
  removeExternalNumber: (index: number) => void;
  isUsedForOutGoingCalls: (index: number) => boolean;
  selectForOutGoingCalls: (index: number) => void;
};

const maximumExternalNumbers = 5;

const useCallPlanExternalNumbers = (callPlanForm: UseFormReturn<CallPlanForm>): CallPlanExternalNumbers => {
  const { t } = useTranslation('careConnectSettings', { keyPrefix: 'callPlanExternalNumbers' });
  const { showConfirmationPopUp } = use(ConfirmationPopupProviderContext);
  const isCallPlanEnabled = useWatch({ control: callPlanForm.control, name: 'isCallPlanEnabled' });
  const { fields: externalNumbers, replace } = useFieldArray({
    control: callPlanForm.control,
    name: 'externalNumbers',
    rules: {
      validate: (numbers) => !isCallPlanEnabled || numbers.length > 0 || t('validationRules.atLeastOneRequired'),
    },
  });
  const externalNumberValues = useWatch({ control: callPlanForm.control, name: 'externalNumbers' });
  const externalNumbersEmptyErrorMessage = callPlanForm.formState.errors.externalNumbers?.root?.message;
  const canAddExternalNumber = externalNumberValues.length < maximumExternalNumbers;

  const addExternalNumber = async (): Promise<void> => {
    const selectedExternalNumberIndex = externalNumberValues.findIndex((externalNumber) => externalNumber.isUsedForOutGoingCalls);
    const selectedIndex = selectedExternalNumberIndex === -1 ? 0 : selectedExternalNumberIndex;

    replace([
      ...externalNumberValues.map((externalNumber, index) => ({
        ...externalNumber,
        isUsedForOutGoingCalls: index === selectedIndex,
      })),
      { number: '', isUsedForOutGoingCalls: externalNumberValues.length === 0 },
    ]);
    await callPlanForm.trigger('externalNumbers');
  };

  const removeExternalNumber = (index: number): void => {
    const remove = (): void => {
      const remainingExternalNumbers = externalNumberValues.filter((_, externalNumberIndex) => externalNumberIndex !== index);
      const hasSelectedExternalNumber = remainingExternalNumbers.some((externalNumber) => externalNumber.isUsedForOutGoingCalls);

      replace(
        remainingExternalNumbers.map((externalNumber, externalNumberIndex) => ({
          ...externalNumber,
          isUsedForOutGoingCalls: hasSelectedExternalNumber ? externalNumber.isUsedForOutGoingCalls : externalNumberIndex === 0,
        })),
      );
    };
    const externalNumberValue = externalNumberValues[index]?.number;
    const externalNumberId = externalNumberValues[index]?.id;

    if (!externalNumberId && !externalNumberValue) {
      remove();
      return;
    }

    showConfirmationPopUp(
      remove,
      t('deleteConfirmationPopup.title'),
      t('deleteConfirmationPopup.body'),
      t('deleteConfirmationPopup.confirm'),
      t('deleteConfirmationPopup.cancel'),
    );
  };

  const isUsedForOutGoingCalls = (index: number): boolean =>
    externalNumberValues.length === 1 || externalNumberValues[index]?.isUsedForOutGoingCalls === true;

  const selectForOutGoingCalls = (index: number): void => {
    callPlanForm.setValue(
      'externalNumbers',
      externalNumberValues.map((externalNumber, externalNumberIndex) => ({
        ...externalNumber,
        isUsedForOutGoingCalls: externalNumberIndex === index,
      })),
      { shouldDirty: true },
    );
  };

  return {
    externalNumbers,
    externalNumbersEmptyErrorMessage,
    canAddExternalNumber,
    addExternalNumber,
    removeExternalNumber,
    isUsedForOutGoingCalls,
    selectForOutGoingCalls,
  };
};

export default useCallPlanExternalNumbers;
